import React, { useEffect, useRef, useState } from 'react';

import { Loader } from '../../../components';
import { Button, PrimaryButton } from '../../../components/common';
import { PrimaryButtonColor } from '../../../components/common/buttons/decorators/PrimaryButton/PrimaryButton';
import { createAuthTokenManager, createProfileManager } from '../../../di';
import { useAppDispatch, useNavigateByName, useProfileData } from '../../../hooks';
import { set } from '../../../store/profile';
import { RouteDictionary } from '../../../utils';
import styles from './RedirectAfterAuth.module.scss';

const PROFILE_RETRY_DELAYS = [1000, 2000, 4000, 8000, 8000];

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const RedirectAfterAuth = () => {
  const navigate = useNavigateByName();
  const profileData = useProfileData();
  const dispatch = useAppDispatch();
  const [failed, setFailed] = useState(false);
  const cancelledRef = useRef(false);

  const redirectByVerified = (lenderVerified: boolean) => {
    if (lenderVerified) {
      navigate(RouteDictionary.PRIMARY_MARKET);
    } else navigate(RouteDictionary.MY_MONEY);
  };

  const loadProfile = async () => {
    setFailed(false);
    const manager = await createProfileManager();
    for (const delayMs of [0, ...PROFILE_RETRY_DELAYS]) {
      if (cancelledRef.current) {
        return;
      }
      if (delayMs > 0) {
        await delay(delayMs);
      }
      try {
        const profile = await manager.get();
        if (cancelledRef.current) {
          return;
        }
        await dispatch(set(profile));
        redirectByVerified(profile.lender?.verified ?? false);
        return;
      } catch {
        continue;
      }
    }
    if (!cancelledRef.current) {
      setFailed(true);
    }
  };

  useEffect(() => {
    cancelledRef.current = false;
    (async () => {
      const tokenExists = await createAuthTokenManager().has();
      if (!tokenExists) {
        navigate(RouteDictionary.ENTER);
        return;
      }
      if (profileData.uuid) {
        redirectByVerified(profileData.lenderVerified);
        return;
      }
      await loadProfile();
    })();
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  if (failed) {
    return (
      <div className={styles.section}>
        <p>Не удалось загрузить профиль. Пожалуйста, попробуйте ещё раз.</p>
        <PrimaryButton color={PrimaryButtonColor.GREEN}>
          <Button onClick={loadProfile}>Повторить</Button>
        </PrimaryButton>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <p>Перенаправляем вас, пожалуйста, подождите...</p>
      <Loader />
    </div>
  );
};

export default RedirectAfterAuth;
